import classnames from "classnames";
import { debounce } from "lodash";
import React, { useContext, useEffect } from "react";
import { RicosContext } from "./../../ricos-context";
import { Node_Type } from "./../../ricos-schema/legacy/wix/rich_content/v1";
import { isDefined } from "./../../wix-rich-content-common";
import Tooltip from "./../../wix-rich-content-common/lib/Tooltip";
import {
  FILE_UPLOAD_EXTENSION,
  FILE_UPLOAD_NAME,
  FILE_UPLOAD_NAME_CONTAINER,
  ROOT
} from "../dataHooks";
import { DownloadIcon, ErrorIcon, getIcon, LoaderIcon, ReadyIcon } from "../icons";
import styles from "../statics/styles/file-upload-viewer-scss";
import { FILE_UPLOAD_TYPE } from "../types";
const getNameWithoutType = (fileName) => {
  if (!fileName || !fileName.includes(".")) {
    return fileName;
  }
  const s = fileName.split(".");
  return s.slice(0, s.length - 1).join(".");
};
const resizeWidths = { first: 320, second: 140, third: 100 };
const FileViewer = ({ settings, componentData, isLoading = false, tempDataPlaceHolder, helpers, nodeId }) => {
  const { isMobile, t, tabIndex } = useContext(RicosContext);
  const downloaderRef = React.useRef(null);
  const [shouldDownloadOnRender, setShouldDownloadOnRender] = React.useState(false);
  const [fileUploadViewerRef, setFileUploadViewerRef] = React.useState(null);
  const [resolvedFileUrl, setResolvedFileUrl] = React.useState(void 0);
  const [resolvingUrl, setResolvingUrl] = React.useState(false);
  const [showReadyIcon, setShowReadyIcon] = React.useState(false);
  const [_, setCurrentWidth] = React.useState(0);
  useEffect(() => {
    if (!fileUploadViewerRef || !window?.ResizeObserver) {
      return;
    }
    const resizer = new ResizeObserver(
      debounce((entries) => {
        setCurrentWidth(Math.round(entries[0].contentRect.width));
      }, 60)
    );
    resizer.observe(fileUploadViewerRef);
    return () => {
      resizer.unobserve(fileUploadViewerRef);
    };
  }, [fileUploadViewerRef]);
  useEffect(() => {
    if (!shouldDownloadOnRender || !downloaderRef.current) {
      return;
    }
    downloaderRef.current?.click();
    setShouldDownloadOnRender(false);
  }, [shouldDownloadOnRender, downloaderRef.current]);
  useEffect(() => {
    if (isLoading) {
      switchReadyIcon();
    }
  }, [isLoading]);
  const switchReadyIcon = () => {
    setShowReadyIcon(true);
    setTimeout(() => setShowReadyIcon(false), 2e3);
  };
  const getIsInResizeRange = (resizeWidth) => fileUploadViewerRef?.clientWidth && fileUploadViewerRef.clientWidth < resizeWidth;
  const renderContainerWithoutLink = () => {
    const { name, type } = tempDataPlaceHolder ? tempDataPlaceHolder : componentData;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: getIsInResizeRange(resizeWidths.third)
        })
      },
      isDefined(name) && isDefined(type) && renderViewerBody({ name, type })
    );
  };
  const renderIcon = (Icon) => {
    const showLoader = isLoading || resolvingUrl;
    const showFileIcon = (!showLoader && !showReadyIcon && isMobile || !isMobile) && !!Icon;
    if (showFileIcon) {
      return /* @__PURE__ */ React.createElement(
        Icon,
        {
          styles,
          className: classnames(styles.file_upload_type_icon, {
            [styles.width_three]: getIsInResizeRange(resizeWidths.third)
          })
        }
      );
    }
    return /* @__PURE__ */ React.createElement("div", { className: isMobile ? styles.mobile_status_icon : styles.file_upload_state }, componentData.error ? /* @__PURE__ */ React.createElement(ErrorIcon, null) : showLoader ? /* @__PURE__ */ React.createElement(LoaderIcon, { className: styles.file_loader_icon }) : showReadyIcon ? /* @__PURE__ */ React.createElement(ReadyIcon, null) : !getIsInResizeRange(resizeWidths.first) && /* @__PURE__ */ React.createElement(DownloadIcon, null));
  };
  const getFileInfoString = (type) => {
    if (componentData.error) {
      return {
        infoString: t("UploadFile_Error_Generic_Item"),
        infoStyle: styles.file_upload_text_error
      };
    }
    const translationKey = isLoading || resolvingUrl ? "UploadFile_Viewer_Loader" : "UploadFile_Viewer_Download";
    const fileType = type?.toUpperCase() ?? "";
    const formattedFileSize = getFormattedFileSize(componentData);
    const fileSizeSuffix = formattedFileSize ? ` • ${formattedFileSize}` : "";
    return {
      infoString: `${t(translationKey, { fileType })}${fileSizeSuffix}`,
      infoStyle: styles.file_upload_type
    };
  };
  const getFormattedFileSize = (componentData2) => {
    const sizeInKb = componentData2.sizeInKb ?? tempDataPlaceHolder?.sizeInKb;
    const size = componentData2.size ?? tempDataPlaceHolder?.size;
    if (sizeInKb && !isNaN(parseFloat(sizeInKb))) {
      return sizeInKbToString(parseFloat(sizeInKb));
    }
    if (size) {
      return sizeToString(size);
    }
    return void 0;
  };
  const sizeInKbToString = (sizeInKb) => {
    if (sizeInKb > 1e3) {
      return `${(sizeInKb / 1e3).toFixed(2)}MB`;
    }
    return `${Math.round(sizeInKb)}KB`;
  };
  const sizeToString = (size) => {
    return size < 1e3 ? size + "B" : size < 1e6 ? Math.round(size / 1e3) + "KB" : (size / 1e6).toFixed(2) + "MB";
  };
  const renderViewerBody = ({ name, type }) => {
    const nameWithoutType = getNameWithoutType(name);
    const Icon = getIcon(type);
    const { infoString, infoStyle } = getFileInfoString(type);
    return /* @__PURE__ */ React.createElement(React.Fragment, null, renderIcon(Icon), !isMobile && renderIcon(), !getIsInResizeRange(resizeWidths.third) && /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classnames(styles.file_upload_text_container, {
          [styles.width_two]: getIsInResizeRange(resizeWidths.second)
        })
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: styles.file_upload_name_container,
          "data-hook": FILE_UPLOAD_NAME_CONTAINER
        },
        !getIsInResizeRange(resizeWidths.second) && /* @__PURE__ */ React.createElement("div", { className: styles.file_upload_name, "data-hook": FILE_UPLOAD_NAME }, nameWithoutType),
        type && /* @__PURE__ */ React.createElement(
          "div",
          {
            className: classnames(styles.file_upload_extension, {
              [styles.width_one]: getIsInResizeRange(resizeWidths.first)
            }),
            "data-hook": FILE_UPLOAD_EXTENSION
          },
          "." + type
        )
      ),
      /* @__PURE__ */ React.createElement("div", { className: infoStyle }, infoString)
    ));
  };
  const renderViewer = (fileUrl) => {
    const { name, type, error } = componentData;
    const { downloadTarget } = settings;
    if (error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }
    return /* @__PURE__ */ React.createElement(
      "a",
      {
        "data-cancel-link": true,
        tabIndex,
        href: fileUrl,
        ref: downloaderRef,
        target: downloadTarget,
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: getIsInResizeRange(resizeWidths.third)
        }),
        onClick: onFileClick,
        download: true
      },
      isDefined(name) && isDefined(type) && renderViewerBody({ name, type })
    );
  };
  const renderFileUrlResolver = () => {
    const { name, type, error } = componentData;
    if (error) {
      return renderContainerWithoutLink();
    }
    const fileUrlResolver = () => {
      if (resolvingUrl) {
        return;
      }
      setResolvingUrl(true);
      settings.resolveFileUrl({
        id: componentData?.src?.id,
        type: componentData.type,
        path: componentData.path,
        mimeType: componentData.mimeType,
        name: componentData.name,
        size: componentData.size,
        pdfSettings: componentData.pdfSettings,
        privacy: componentData?.src?.private ? "private" : "public"
      }).then((resolvedFileUrl2) => {
        setResolvedFileUrl(resolvedFileUrl2);
        setResolvingUrl(false);
        setShouldDownloadOnRender(true);
        switchReadyIcon();
      });
    };
    const resolveIfEnter = (e) => {
      if (e.key === "Enter") {
        fileUrlResolver();
      }
    };
    return /* @__PURE__ */ React.createElement(
      "button",
      {
        type: "button",
        onClick: fileUrlResolver,
        onKeyDown: resolveIfEnter,
        className: classnames(styles.file_upload_link, {
          [styles.width_three]: getIsInResizeRange(resizeWidths.third)
        })
      },
      isDefined(name) && isDefined(type) && renderViewerBody({ name, type })
    );
  };
  const onFileClick = () => {
    if (componentData.name) {
      helpers?.onViewerAction?.(FILE_UPLOAD_TYPE, "Click", componentData.name, nodeId, {
        type: Node_Type.FILE,
        fileData: componentData
      });
    }
  };
  const getViewer = () => {
    const { error } = componentData;
    if (error || tempDataPlaceHolder) {
      return renderContainerWithoutLink();
    }
    const fileUrl = componentData.src?.url || resolvedFileUrl;
    return fileUrl ? renderViewer(fileUrl) : renderFileUrlResolver();
  };
  return /* @__PURE__ */ React.createElement(
    Tooltip,
    {
      content: getIsInResizeRange(resizeWidths.first) && componentData.name,
      tooltipOffset: { y: 25, x: 0 }
    },
    /* @__PURE__ */ React.createElement(
      "div",
      {
        "data-hook": ROOT,
        ref: setFileUploadViewerRef,
        className: classnames(
          styles.file_upload_container,
          componentData.error && styles.file_upload_error_container
        )
      },
      getViewer()
    )
  );
};
export {
  FileViewer
};
//# sourceMappingURL=file-viewer.js.map
